<script lang="ts">
  import { untrack } from 'svelte';
  import Trash2 from '@lucide/svelte/icons/trash-2';
  import Plus from '@lucide/svelte/icons/plus';
  import Download from '@lucide/svelte/icons/download';
  import Upload from '@lucide/svelte/icons/upload';
  import { Button } from '$lib/components/ui/button';
  import * as Card from '$lib/components/ui/card';
  import { Input } from '$lib/components/ui/input';
  import * as Select from '$lib/components/ui/select';
  import { Slider } from '$lib/components/ui/slider';
  import { Switch } from '$lib/components/ui/switch';
  import type { RoomConfig } from '$lib/types';

  type Config = {
    apiUrl: string;
    token: string;
    lang: string;
    primaryColor: string;
    borderRadius: string;
    showShadow: boolean;
    backgroundColor: string;
    showBorder: boolean;
    borderColor: string;
    separateDates: boolean;
    showBookButton: boolean;
    showPricing: boolean;
    rooms: RoomConfig[];
  };

  type LangOption = { value: string; label: string };

  let {
    config,
    defaults,
    langOptions = [] as LangOption[],
    labels = {} as Record<string, string>,
    onapichange,
    onlangchange,
    onvisualchange,
    onlayoutchange,
    onroomschange,
    onreset,
    getSiteDomain,
  }: {
    config: Config;
    defaults: Config;
    langOptions?: LangOption[];
    labels?: Record<string, string>;
    onapichange?: (config: Config) => void;
    onlangchange?: (config: Config) => void;
    onvisualchange?: (config: Config) => void;
    onlayoutchange?: (config: Config) => void;
    onroomschange?: (config: Config) => void;
    onreset?: (config: Config) => void;
    getSiteDomain?: () => string;
  } = $props();

  const DEFAULT_RADIUS_PX = 10;
  const DEFAULT_COLOR = '#18181b';

  function parseBorderRadius(val: string): number {
    if (!val) return DEFAULT_RADIUS_PX;
    const num = parseInt(val, 10);
    return isNaN(num) ? DEFAULT_RADIUS_PX : num;
  }

  const DEFAULT_BG_COLOR = '';

  const _init = untrack(() => ({ ...config }));
  let apiUrl = $state(_init.apiUrl || '');
  let token = $state(_init.token || '');
  let lang = $state(_init.lang || 'fr-FR');
  let colorPickerVal = $state(_init.primaryColor || DEFAULT_COLOR);
  let colorHexVal = $state(_init.primaryColor || DEFAULT_COLOR);
  let borderRadiusPx = $state(parseBorderRadius(_init.borderRadius));
  let showShadow = $state(_init.showShadow !== false);
  let bgColorPickerVal = $state(_init.backgroundColor || DEFAULT_BG_COLOR);
  let bgColorHexVal = $state(_init.backgroundColor || DEFAULT_BG_COLOR);
  let showBorder = $state(_init.showBorder !== false);
  let borderColorPickerVal = $state(_init.borderColor || '');
  let borderColorHexVal = $state(_init.borderColor || '');
  let separateDates = $state(_init.separateDates === true);
  let showBookButton = $state(_init.showBookButton !== false);
  let showPricing = $state(_init.showPricing !== false);
  let rooms = $state<RoomConfig[]>(
    Array.isArray(_init.rooms) ? _init.rooms.map((r) => ({ ...r })) : [],
  );
  let statusText = $state('');
  let statusSaved = $state(false);

  function getConfig(): Config {
    return {
      apiUrl: apiUrl.trim(),
      token: token.trim(),
      lang: lang.trim() || 'fr-FR',
      primaryColor: colorPickerVal === DEFAULT_COLOR ? '' : colorPickerVal,
      borderRadius: borderRadiusPx + 'px',
      showShadow,
      backgroundColor: bgColorPickerVal || '',
      showBorder,
      borderColor: borderColorPickerVal || '',
      separateDates,
      showBookButton,
      showPricing,
      rooms: rooms.map((r) => ({ ...r })),
    };
  }

  function newRoomId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return 'room-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
  }

  function handleAddRoom() {
    rooms = [
      ...rooms,
      {
        id: newRoomId(),
        label: '',
        idRoom: '',
        roomtcode: '',
        catcode: '',
      },
    ];
    onroomschange?.(getConfig());
  }

  function handleRemoveRoom(id: string) {
    rooms = rooms.filter((r) => r.id !== id);
    onroomschange?.(getConfig());
  }

  function handleRoomFieldChange() {
    onroomschange?.(getConfig());
  }

  function handleApiTokenChange() {
    onapichange?.(getConfig());
  }

  const triggerLabel = $derived(
    langOptions.find((o) => o.value === lang)?.label ?? lang
  );

  function handleLangChange(v: string) {
    lang = v;
    onlangchange?.(getConfig());
  }

  function handleColorPickerInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    colorPickerVal = val;
    colorHexVal = val;
    onvisualchange?.(getConfig());
  }

  function handleColorHexInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    colorHexVal = val;
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      colorPickerVal = val;
      onvisualchange?.(getConfig());
    } else if (val.trim() === '') {
      colorPickerVal = DEFAULT_COLOR;
      onvisualchange?.(getConfig());
    }
  }

  function handleRadiusChange(v: number) {
    borderRadiusPx = v;
    onvisualchange?.(getConfig());
  }

  function handleShadowChange(checked: boolean) {
    showShadow = checked;
    onvisualchange?.(getConfig());
  }

  function handleBgColorPickerInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    bgColorPickerVal = val;
    bgColorHexVal = val;
    onvisualchange?.(getConfig());
  }

  function handleBgColorHexInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    bgColorHexVal = val;
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      bgColorPickerVal = val;
      onvisualchange?.(getConfig());
    } else if (val.trim() === '') {
      bgColorPickerVal = '';
      onvisualchange?.(getConfig());
    }
  }

  function handleBorderToggle(checked: boolean) {
    showBorder = checked;
    onvisualchange?.(getConfig());
  }

  function handleBorderColorPickerInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    borderColorPickerVal = val;
    borderColorHexVal = val;
    onvisualchange?.(getConfig());
  }

  function handleBorderColorHexInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    borderColorHexVal = val;
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      borderColorPickerVal = val;
      onvisualchange?.(getConfig());
    } else if (val.trim() === '') {
      borderColorPickerVal = '';
      onvisualchange?.(getConfig());
    }
  }

  function handleSeparateDatesToggle(checked: boolean) {
    separateDates = checked;
    onlayoutchange?.(getConfig());
  }

  function handleShowBookButtonToggle(checked: boolean) {
    showBookButton = checked;
    onlayoutchange?.(getConfig());
  }

  function handleShowPricingToggle(checked: boolean) {
    showPricing = checked;
    onlayoutchange?.(getConfig());
  }

  let flashTimeout: ReturnType<typeof setTimeout>;
  let fileInput: HTMLInputElement;

  function buildExportFilename(): string {
    const domain = getSiteDomain?.();
    const slug = domain
      ? domain.replace(/[^a-zA-Z0-9.-]/g, '_').replace(/^\.+|\.+$/g, '')
      : '';
    return slug
      ? `quick-search-config-${slug}.json`
      : 'quick-search-config.json';
  }

  function handleExport() {
    const cfg = getConfig();
    const json = JSON.stringify(cfg, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = buildExportFilename();
    a.click();
    URL.revokeObjectURL(url);
  }

  function applyConfig(cfg: Config) {
    apiUrl = cfg.apiUrl || '';
    token = cfg.token || '';
    lang = cfg.lang || 'fr-FR';
    colorPickerVal = cfg.primaryColor || DEFAULT_COLOR;
    colorHexVal = cfg.primaryColor || DEFAULT_COLOR;
    borderRadiusPx = parseBorderRadius(cfg.borderRadius);
    showShadow = cfg.showShadow !== false;
    bgColorPickerVal = cfg.backgroundColor || DEFAULT_BG_COLOR;
    bgColorHexVal = cfg.backgroundColor || DEFAULT_BG_COLOR;
    showBorder = cfg.showBorder !== false;
    borderColorPickerVal = cfg.borderColor || '';
    borderColorHexVal = cfg.borderColor || '';
    separateDates = cfg.separateDates === true;
    showBookButton = cfg.showBookButton !== false;
    showPricing = cfg.showPricing !== false;
    rooms = Array.isArray(cfg.rooms)
      ? cfg.rooms.map((r) => ({ ...r }))
      : [];
  }

  function handleImport() {
    fileInput.click();
  }

  function handleFileSelected(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (typeof parsed !== 'object' || parsed === null || !parsed.apiUrl) {
          throw new Error('Invalid config');
        }
        applyConfig(parsed as Config);

        statusText = labels.statusImported || 'Configuration importée';
        statusSaved = true;
        clearTimeout(flashTimeout);
        flashTimeout = setTimeout(() => {
          statusText = '';
          statusSaved = false;
        }, 2500);

        onapichange?.(getConfig());
        onlangchange?.(getConfig());
        onvisualchange?.(getConfig());
        onlayoutchange?.(getConfig());
        onroomschange?.(getConfig());
      } catch {
        statusText = labels.statusImportError || 'Fichier JSON invalide';
        statusSaved = false;
        clearTimeout(flashTimeout);
        flashTimeout = setTimeout(() => {
          statusText = '';
        }, 3000);
      }
    };
    reader.readAsText(file);
    (e.target as HTMLInputElement).value = '';
  }

  function handleReset() {
    apiUrl = defaults.apiUrl || '';
    token = defaults.token || '';
    lang = defaults.lang || 'fr-FR';
    colorPickerVal = defaults.primaryColor || DEFAULT_COLOR;
    colorHexVal = defaults.primaryColor || DEFAULT_COLOR;
    borderRadiusPx = parseBorderRadius(defaults.borderRadius);
    showShadow = defaults.showShadow !== false;
    bgColorPickerVal = defaults.backgroundColor || DEFAULT_BG_COLOR;
    bgColorHexVal = defaults.backgroundColor || DEFAULT_BG_COLOR;
    showBorder = defaults.showBorder !== false;
    borderColorPickerVal = defaults.borderColor || '';
    borderColorHexVal = defaults.borderColor || '';
    separateDates = defaults.separateDates === true;
    showBookButton = defaults.showBookButton !== false;
    showPricing = defaults.showPricing !== false;
    rooms = Array.isArray(defaults.rooms)
      ? defaults.rooms.map((r) => ({ ...r }))
      : [];

    statusText = labels.statusReset || '';
    statusSaved = true;
    clearTimeout(flashTimeout);
    flashTimeout = setTimeout(() => {
      statusText = '';
      statusSaved = false;
    }, 2500);

    onreset?.(getConfig());
  }
</script>

<form
  class="bg-background p-5 rounded-xl shadow-sm mt-6 grid gap-3.5"
  autocomplete="off"
  onsubmit={(e) => e.preventDefault()}
>
  <h2 class="m-0 text-sm font-semibold uppercase tracking-wider">
    {labels.configTitle || 'Configuration'}
  </h2>

  <div class="flex flex-col gap-1.5">
    <label class="text-sm font-medium text-muted-foreground" for="cfg-api-url">
      {labels.apiUrl || 'API URL'}
    </label>
    <Input
      id="cfg-api-url"
      type="url"
      bind:value={apiUrl}
      required
      placeholder="https://api-next.eliophot.com"
      onchange={handleApiTokenChange}
    />
  </div>

  <div class="flex flex-col gap-1.5">
    <label class="text-sm font-medium text-muted-foreground" for="cfg-token">
      {labels.token || 'Token'}
    </label>
    <Input
      id="cfg-token"
      type="text"
      bind:value={token}
      required
      placeholder={labels.placeholderToken || ''}
      onchange={handleApiTokenChange}
    />
  </div>

  <div class="flex flex-col gap-1.5">
    <span class="text-sm font-medium text-muted-foreground">
      {labels.lang || 'Langue'}
    </span>
    <Select.Root type="single" bind:value={lang} onValueChange={handleLangChange}>
      <Select.Trigger class="w-full">
        {triggerLabel}
      </Select.Trigger>
      <Select.Content class="max-h-[300px]" portalProps={{ disabled: true }}>
        {#each langOptions as opt (opt.value)}
          <Select.Item value={opt.value} label={opt.label}>
            {opt.label}
          </Select.Item>
        {/each}
      </Select.Content>
    </Select.Root>
  </div>

  <div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-medium text-muted-foreground" for="cfg-color-hex">
        {labels.primaryColor || 'Couleur primaire'}
      </label>
      <div class="flex items-center gap-2">
        <input
          type="color"
          value={colorPickerVal}
          oninput={handleColorPickerInput}
          aria-label="Color picker"
          class="border-input w-10 h-10 p-0.5 border rounded-md cursor-pointer shrink-0"
        />
        <Input
          id="cfg-color-hex"
          type="text"
          value={colorHexVal}
          placeholder="#18181b"
          pattern="^#[0-9a-fA-F]{6}$"
          maxlength={7}
          class="font-mono uppercase"
          oninput={handleColorHexInput}
        />
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-muted-foreground">
        {labels.borderRadius || 'Border radius'}
      </span>
      <div class="flex items-center gap-3 min-h-10">
        <Slider
          type="single"
          bind:value={borderRadiusPx}
          onValueChange={handleRadiusChange}
          max={24}
          step={1}
          class="flex-1"
          aria-label="Border radius"
        />
        <span class="min-w-[3.5ch] text-right text-sm tabular-nums font-medium text-foreground">
          {borderRadiusPx}px
        </span>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-medium text-muted-foreground" for="cfg-bg-color-hex">
        {labels.backgroundColor || 'Couleur de fond'}
      </label>
      <div class="flex items-center gap-2">
        <input
          type="color"
          value={bgColorPickerVal || '#ffffff'}
          oninput={handleBgColorPickerInput}
          aria-label="Background color picker"
          class="border-input w-10 h-10 p-0.5 border rounded-md cursor-pointer shrink-0"
        />
        <Input
          id="cfg-bg-color-hex"
          type="text"
          value={bgColorHexVal}
          placeholder="#ffffff"
          pattern="^#[0-9a-fA-F]{6}$"
          maxlength={7}
          class="font-mono uppercase"
          oninput={handleBgColorHexInput}
        />
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-muted-foreground">
        {labels.showShadow || 'Ombre portée'}
      </span>
      <div class="flex items-center gap-2 min-h-10">
        <Switch
          id="cfg-show-shadow"
          checked={showShadow}
          onCheckedChange={handleShadowChange}
        />
        <label for="cfg-show-shadow" class="text-sm text-foreground cursor-pointer">
          {showShadow ? (labels.shadowOn || 'Activée') : (labels.shadowOff || 'Désactivée')}
        </label>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
    <div class="flex flex-col gap-1.5">
      <label class="text-sm font-medium text-muted-foreground" for="cfg-border-color-hex">
        {labels.borderColor || 'Couleur de bordure'}
      </label>
      <div class="flex items-center gap-2">
        <input
          type="color"
          value={borderColorPickerVal || '#e4e4e7'}
          oninput={handleBorderColorPickerInput}
          aria-label="Border color picker"
          class="border-input w-10 h-10 p-0.5 border rounded-md cursor-pointer shrink-0"
        />
        <Input
          id="cfg-border-color-hex"
          type="text"
          value={borderColorHexVal}
          placeholder="#e4e4e7"
          pattern="^#[0-9a-fA-F]{6}$"
          maxlength={7}
          class="font-mono uppercase"
          oninput={handleBorderColorHexInput}
        />
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-muted-foreground">
        {labels.showBorder || 'Bordures'}
      </span>
      <div class="flex items-center gap-2 min-h-10">
        <Switch
          id="cfg-show-border"
          checked={showBorder}
          onCheckedChange={handleBorderToggle}
        />
        <label for="cfg-show-border" class="text-sm text-foreground cursor-pointer">
          {showBorder ? (labels.borderOn || 'Activées') : (labels.borderOff || 'Désactivées')}
        </label>
      </div>
    </div>
  </div>

  <div class="flex flex-col gap-1.5">
    <span class="text-sm font-medium text-muted-foreground">
      {labels.separateDates || 'Séparer les dates'}
    </span>
    <div class="flex items-center gap-2 min-h-10">
      <Switch
        id="cfg-separate-dates"
        checked={separateDates}
        onCheckedChange={handleSeparateDatesToggle}
      />
      <label for="cfg-separate-dates" class="text-sm text-foreground cursor-pointer">
        {separateDates
          ? (labels.separateDatesOn || 'Activé')
          : (labels.separateDatesOff || 'Désactivé')}
      </label>
    </div>
  </div>

  <div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-muted-foreground">
        {labels.showBookButton || 'Bouton Réserver'}
      </span>
      <div class="flex items-center gap-2 min-h-10">
        <Switch
          id="cfg-show-book-button"
          checked={showBookButton}
          onCheckedChange={handleShowBookButtonToggle}
        />
        <label for="cfg-show-book-button" class="text-sm text-foreground cursor-pointer">
          {showBookButton
            ? (labels.bookButtonOn || 'Affiché')
            : (labels.bookButtonOff || 'Masqué')}
        </label>
      </div>
    </div>

    <div class="flex flex-col gap-1.5">
      <span class="text-sm font-medium text-muted-foreground">
        {labels.showPricing || 'Remontée des prix'}
      </span>
      <div class="flex items-center gap-2 min-h-10">
        <Switch
          id="cfg-show-pricing"
          checked={showPricing}
          onCheckedChange={handleShowPricingToggle}
        />
        <label for="cfg-show-pricing" class="text-sm text-foreground cursor-pointer">
          {showPricing
            ? (labels.pricingOn || 'Affichée')
            : (labels.pricingOff || 'Masquée')}
        </label>
      </div>
    </div>
  </div>

  <div class="mt-2 flex flex-col gap-2">
    <h3 class="m-0 text-sm font-semibold uppercase tracking-wider">
      {labels.roomsTitle || 'Configurations de chambres'}
    </h3>

    {#if rooms.length === 0}
      <p class="m-0 text-xs text-muted-foreground">
        {labels.roomsEmptyHint ||
          'Aucune chambre configurée. Ajoutez-en pour générer un mini widget.'}
      </p>
    {:else}
      <div class="flex flex-col gap-2">
        {#each rooms as room (room.id)}
          <Card.Root size="sm" class="px-3 py-3">
            <div
              class="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1.2fr_1fr_1fr_1fr_auto]"
            >
              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-muted-foreground"
                  for="cfg-room-label-{room.id}"
                >
                  {labels.roomsLabel || 'Libellé'}
                </label>
                <Input
                  id="cfg-room-label-{room.id}"
                  type="text"
                  bind:value={room.label}
                  placeholder="Suite Junior"
                  onchange={handleRoomFieldChange}
                />
              </div>

              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-muted-foreground"
                  for="cfg-room-id-{room.id}"
                >
                  {labels.roomsIdRoom || 'id_room (Dedge)'}
                </label>
                <Input
                  id="cfg-room-id-{room.id}"
                  type="text"
                  bind:value={room.idRoom}
                  placeholder="123"
                  onchange={handleRoomFieldChange}
                />
              </div>

              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-muted-foreground"
                  for="cfg-room-rtc-{room.id}"
                >
                  {labels.roomsRoomtcode || 'roomtcode (Reservit)'}
                </label>
                <Input
                  id="cfg-room-rtc-{room.id}"
                  type="text"
                  bind:value={room.roomtcode}
                  placeholder="STD"
                  onchange={handleRoomFieldChange}
                />
              </div>

              <div class="flex flex-col gap-1">
                <label
                  class="text-xs font-medium text-muted-foreground"
                  for="cfg-room-cat-{room.id}"
                >
                  {labels.roomsCatcode || 'catcode (Reservit)'}
                </label>
                <Input
                  id="cfg-room-cat-{room.id}"
                  type="text"
                  bind:value={room.catcode}
                  placeholder="CAT1"
                  onchange={handleRoomFieldChange}
                />
              </div>

              <Button
                variant="ghost"
                size="icon-sm"
                type="button"
                aria-label={labels.roomsRemove || 'Supprimer'}
                onclick={() => handleRemoveRoom(room.id)}
                class="self-end text-muted-foreground hover:text-destructive"
              >
                <Trash2 />
              </Button>
            </div>
          </Card.Root>
        {/each}
      </div>
    {/if}

    <div>
      <Button variant="outline" size="sm" type="button" onclick={handleAddRoom}>
        <Plus />
        {labels.roomsAdd || 'Ajouter une chambre'}
      </Button>
    </div>
  </div>

  <input
    bind:this={fileInput}
    type="file"
    accept=".json,application/json"
    class="hidden"
    onchange={handleFileSelected}
  />

  <div class="flex flex-wrap gap-2 items-center mt-1">
    <Button variant="outline" type="button" onclick={handleReset}>
      {labels.reset || 'Réinitialiser'}
    </Button>
    <Button variant="outline" type="button" onclick={handleExport}>
      <Download />
      {labels.exportConfig || 'Exporter'}
    </Button>
    <Button variant="outline" type="button" onclick={handleImport}>
      <Upload />
      {labels.importConfig || 'Importer'}
    </Button>
    <span
      class="text-xs min-h-4"
      class:text-muted-foreground={!statusSaved}
      class:text-green-600={statusSaved}
      aria-live="polite"
    >{statusText}</span>
  </div>
</form>
